import { Capacitor, registerPlugin } from "@capacitor/core";
import { App } from "@capacitor/app";
import { Preferences } from "@capacitor/preferences";
import { Browser } from "@capacitor/browser";
import { Keyboard } from "@capacitor/keyboard";
import { InAppReview } from "@capacitor-community/in-app-review";
import {
  isBillingUrl,
  isFirstPartyUrl,
  resolveLinkTarget,
  resolveNotificationTarget,
} from "./notificationTarget";
import { rememberReturnDestination } from "./returnDestination";
import { addPushTapListener, unregisterPush } from "./push";
import { PushTap, pushTapPath } from "./pushTap";
// How the native seam works and what must not change: see ./CLAUDE.md
/**
 * The single platform seam. Every native (Capacitor) branch in the app goes
 * through this module — components never import Capacitor APIs directly, so
 * platform checks can't sprawl and the web bundle behaviour is untouched.
 */
export const isNative = (): boolean => Capacitor.isNativePlatform();

export const nativePlatform = (): "ios" | "android" | null => {
  if (!isNative()) return null;
  return Capacitor.getPlatform() as "ios" | "android";
};

// --- Build identity (native only) -----------------------------------------

export interface AppBuildInfo {
  /** CFBundleVersion / versionCode — a monotonic integer, never a version string. */
  build: number;
  platform: "ios" | "android";
}

let buildInfo: Promise<AppBuildInfo | null> | null = null;

/**
 * Which binary is this. Null on web, where the bundle always matches the API
 * it was deployed with and there is nothing to compare.
 *
 * The BUILD NUMBER is the identity, not the marketing version: it is an
 * integer that only ever increases, so "too old" is one comparison. Marketing
 * versions are strings where "1.10" sorts below "1.9".
 *
 * Cached as a resolved promise because it is read on every outbound request;
 * the value cannot change without the process restarting.
 */
export const getAppBuildInfo = async (): Promise<AppBuildInfo | null> => {
  if (!isNative()) return null;
  buildInfo ??= App.getInfo()
    .then((info) => {
      const build = Number(info.build);
      const platform = nativePlatform();
      if (!Number.isFinite(build) || platform === null) return null;
      return { build, platform };
    })
    .catch(() => null);
  return buildInfo;
};

/**
 * Fires when the app returns to the foreground. Returns an unsubscribe; a
 * no-op on web.
 *
 * The update gate needs this: a binary backgrounded for weeks would otherwise
 * never re-check its config, which is exactly the binary most likely to have
 * gone stale.
 */
export const onAppResume = (callback: () => void): (() => void) => {
  if (!isNative()) return () => undefined;
  const handle = App.addListener("resume", callback);
  return () => {
    void handle.then((h) => h.remove());
  };
};

// --- Bearer session token (native only) ---------------------------------
// The WebView's capacitor:// origin cannot hold cross-site cookies, so native
// clients authenticate with the better-auth bearer token instead. The token
// arrives in the `set-auth-token` response header at sign-in and is persisted
// across launches in Capacitor Preferences (app-sandboxed storage); an
// in-memory cache avoids a native bridge round-trip per request.

const AUTH_TOKEN_KEY = "insight:auth-token";

let cachedToken: string | null = null;
let hydration: Promise<void> | null = null;

const hydrateToken = (): Promise<void> => {
  hydration ??= Preferences.get({ key: AUTH_TOKEN_KEY }).then(({ value }) => {
    cachedToken ??= value;
  });
  return hydration;
};

export const getAuthToken = async (): Promise<string | null> => {
  if (!isNative()) return null;
  await hydrateToken();
  return cachedToken;
};

export const setAuthToken = async (token: string): Promise<void> => {
  cachedToken = token;
  hydration = Promise.resolve();
  await Preferences.set({ key: AUTH_TOKEN_KEY, value: token });
};

export const clearAuthToken = async (): Promise<void> => {
  cachedToken = null;
  hydration = Promise.resolve();
  await Preferences.remove({ key: AUTH_TOKEN_KEY });
};

// --- Keyboard visibility (native only) -------------------------------------

/**
 * Subscribe to the native keyboard opening/closing. Returns an unsubscribe
 * function; a no-op on web. Fires on the will-show/will-hide events so UI
 * (e.g. sliding the bottom tab bar away) moves with the keyboard animation
 * rather than after it.
 */
export const onKeyboardToggle = (
  onChange: (visible: boolean) => void,
): (() => void) => {
  if (!isNative()) return () => undefined;
  const show = Keyboard.addListener("keyboardWillShow", () => onChange(true));
  const hide = Keyboard.addListener("keyboardWillHide", () => onChange(false));
  return () => {
    void show.then((handle) => handle.remove());
    void hide.then((handle) => handle.remove());
  };
};

// --- Native onboarding intro ----------------------------------------------
// localStorage survives app relaunches in the WebView; the intro is a
// once-per-install surface, not synced state.

const INTRO_SEEN_KEY = "insight:native-intro-seen";

export const hasSeenNativeIntro = (): boolean => {
  try {
    return localStorage.getItem(INTRO_SEEN_KEY) === "true";
  } catch {
    return true;
  }
};

export const markNativeIntroSeen = (): void => {
  try {
    localStorage.setItem(INTRO_SEEN_KEY, "true");
  } catch {
    // Storage unavailable — the intro just shows again next launch
  }
};

// --- Bottom-edge gesture deferral (iOS only) --------------------------------
// Local native plugin (ios/App/App/EdgeGuard.swift): while enabled, the first
// home-swipe is deferred and delivered to the WebView instead of leaving the
// app; the second swipe always exits (iOS guarantees it — that is also the
// App Review-safe posture). The paywall toggles this around its mount so the
// exit-offer sheet can open ON the swipe, not only on resume.

interface EdgeGuardPlugin {
  setDeferred(options: { enabled: boolean }): Promise<void>;
}

const EdgeGuard = registerPlugin<EdgeGuardPlugin>("EdgeGuard");

export const setBottomEdgeDeferred = async (enabled: boolean): Promise<void> => {
  if (nativePlatform() !== "ios") return;
  try {
    await EdgeGuard.setDeferred({ enabled });
  } catch {
    // Binary predates the native class — the resume trigger still works
  }
};

// --- App Store rating prompt ------------------------------------------------

/**
 * Ask the OS to show its in-app rating sheet (SKStoreReviewController on iOS,
 * Google In-App Review on Android). Fire-and-forget by design: the OS decides
 * whether the sheet actually appears (Apple caps it at 3 shows per user per
 * 365 days and the API reports nothing back), so callers must continue
 * identically whether it showed or not.
 */
export const requestStoreReview = async (): Promise<void> => {
  if (!isNative()) return;
  try {
    await InAppReview.requestReview();
  } catch {
    // The prompt is garnish — never let it interrupt a flow
  }
};

// --- Navigation helpers ---------------------------------------------------

/**
 * Open an external URL: system in-app browser sheet on native (the WebView
 * itself must never navigate away from the app), new tab on web.
 */
export const openExternal = async (url: string): Promise<void> => {
  if (isNative()) {
    await Browser.open({ url });
    return;
  }
  window.open(url, "_blank");
};

/**
 * Native only: route every external-link click through the in-app browser
 * sheet (SFSafariViewController / Custom Tabs) instead of letting the WebView
 * navigate away or bounce to the external Safari app. One capture-phase
 * listener covers every `<a href="http...">` in the app — news articles,
 * company websites, anything future. Router links (relative hrefs) and the
 * dev-server origin are untouched, and React onClick handlers (analytics)
 * still fire because only the default navigation is prevented.
 *
 * Two things never reach the browser sheet. A link to our OWN web origin is an
 * in-app route wearing an external URL — the native origin is
 * capacitor://localhost, so the origin check below cannot recognise it, and
 * opening it would put the web app (billing UI included) in a browser inside
 * the app. It is routed in-app through `navigateInApp` when the destination
 * guard allows it, and dropped otherwise. And a Lemon Squeezy link is dropped
 * outright (App Store rule 3.1.1).
 */
export const installNativeLinkInterceptor = (
  navigateInApp: (path: string) => void,
): void => {
  if (!isNative()) return;
  document.addEventListener(
    "click",
    (event) => {
      const target = event.target as HTMLElement | null;
      const anchor = target?.closest?.("a[href]");
      if (!anchor) return;
      const href = anchor.getAttribute("href") ?? "";
      if (!/^https?:\/\//i.test(href)) return;
      if (href.startsWith(window.location.origin)) return;
      event.preventDefault();
      if (isFirstPartyUrl(href)) {
        const resolved = resolveLinkTarget(href, { isNative: true });
        if (resolved.kind === "internal") navigateInApp(resolved.path);
        return;
      }
      if (isBillingUrl(href)) return;
      void Browser.open({ url: href });
    },
    true,
  );
};

// --- Native entry points: Universal Links and push taps ---------------------
// Both arrive from OUTSIDE the React tree, possibly before it has rendered
// (Capacitor holds the link or tap that launched the app until the first
// listener is added), and possibly while signed out.

/**
 * A Universal Link as an in-app path, or null when it is not ours to open.
 * Only our own web origins qualify (a Google sign-in callback also arrives as
 * an app URL), and its `pathname + search` then passes the same destination
 * guard as a notification — allow-list, /n/<uuid> shape, native billing block
 * and all.
 */
export const resolveAppUrlPath = (url: string): string | null => {
  if (!isFirstPartyUrl(url)) return null;
  const { pathname, search } = new URL(url);
  const resolved = resolveNotificationTarget(`${pathname}${search}`, null, { isNative: true });
  return resolved.kind === "internal" ? resolved.path : null;
};

// Signed out (no bearer token), the remembered destination is the whole
// mechanism: AuthProtectedRoute consumes it after sign-in, and the sign-in or
// onboarding screen is left alone. Signed in, the path is navigated to — and
// remembered first all the same, as the net under a cold start: this can land
// in the same few milliseconds as the root route's own redirect and lose to
// it, and that bounce ends on /dashboard, inside AuthProtectedRoute, which then
// finishes the job. AuthProtectedRoute clears the value on every signed-in
// arrival, so it never lingers. A token that turns out to be dead is its to
// handle too.
const openInApp = async (path: string, navigateInApp: (path: string) => void): Promise<void> => {
  const token = await getAuthToken();
  const currentPath = `${window.location.pathname}${window.location.search}`;
  if (token !== null && path === currentPath) return;
  rememberReturnDestination(path);
  if (token !== null) navigateInApp(path);
};

/**
 * Native only: open Universal Links (an emailed /n/<id> link, a publication)
 * inside the app. AppDelegate already forwards `continue userActivity`.
 */
export const installUniversalLinkHandler = (navigateInApp: (path: string) => void): void => {
  if (!isNative()) return;
  App.addListener("appUrlOpen", ({ url }) => {
    const path = resolveAppUrlPath(url);
    if (path !== null) void openInApp(path, navigateInApp);
  }).catch(() => undefined);
};

let mountedPushTapHandler: ((tap: PushTap) => void) | null = null;

/**
 * The authenticated shell's own way of opening a tap (usePushNotifications):
 * straight through openNotification, with no waypoint route. Returns an
 * unsubscribe.
 */
export const setPushTapHandler = (handler: (tap: PushTap) => void): (() => void) => {
  mountedPushTapHandler = handler;
  return () => {
    if (mountedPushTapHandler === handler) mountedPushTapHandler = null;
  };
};

/**
 * Native only: the one push-tap listener, installed at startup so a tap is
 * never missed. With the authenticated shell mounted it hands the tap over;
 * otherwise (cold start, signed out, the paywall) the tap becomes its
 * /n/<id>?s=push route, navigated to or remembered across sign-in.
 */
export const installPushTapRouting = (navigateInApp: (path: string) => void): void => {
  addPushTapListener((tap) => {
    if (mountedPushTapHandler) {
      mountedPushTapHandler(tap);
      return;
    }
    void openInApp(pushTapPath(tap), navigateInApp);
  });
};

/**
 * Hard-redirect for "session is gone" handling. On native the stored token is
 * cleared first and the WebView reloads from the app root (the route guards
 * then land the signed-out user on the native sign-in flow).
 */
export const redirectToSignIn = async (): Promise<void> => {
  if (isNative()) {
    // Push cleanup belongs to EVERY way of becoming signed out, and this one
    // never passes through useLogout. Started before the token is cleared (the
    // API half needs it) and never awaited: both halves are dispatched before
    // the WebView reloads, and nothing may delay the redirect.
    unregisterPush(await getAuthToken()).catch(() => undefined);
    await clearAuthToken();
    window.location.href = "/";
    return;
  }
  window.location.href = "/sign-in";
};
